import { useContext } from 'react'

import { WebsocketContext, WebsocketContextProps } from '@/contexts/websocket'
import { useConsultation } from '@/hooks/useConsultation'
import { useUser } from '@/hooks/useUser'
import { Consultation } from '@/types/consultation'
import { ERROR_CODE } from '@/types/error'
import { WebSocketState } from '@/types/websocket'

type UseWebsocketProps = Omit<WebsocketContextProps, 'setWebsocketState'> & {
  connect: () => Promise<WebSocket>
  disconnect: () => void
}

export const useWebsocket = (): UseWebsocketProps => {
  const { websocket, websocketState, setWebsocketState } = useContext(WebsocketContext)
  const { updateConsultation } = useConsultation()
  const { user } = useUser()

  const handleWebsocketMessage = (event: MessageEvent) => {
    const parsedMessage = JSON.parse(event.data) as Consultation
    console.log('Websocket message received', parsedMessage)

    updateConsultation(parsedMessage)
  }

  const connect = (): Promise<WebSocket> => {
    console.log('Connect to websocket?')

    return new Promise((resolve, reject) => {
      if (websocket.current) return resolve(websocket.current)

      console.log('Connecting to websocket...', user)

      if (!user?.data?.uniqueId) reject(new Error('User unique id not found when trying to connect to websocket'))

      const pathName = window.location.pathname
      const apiKey = pathName.split('/')[1]
      if (!apiKey) throw new Error(ERROR_CODE.NO_API_KEY)

      websocket.current = new WebSocket(`${import.meta.env.VITE_SOCKET_URL}?uniqueId=${user?.data?.uniqueId}&token=${apiKey}&version=v3`)

      websocket.current.onopen = () => {
        console.log('Websocket connected', websocket.current)
        setWebsocketState(WebSocketState.OPEN)

        // Send message to server to get current state
        websocket.current?.send(
          JSON.stringify({
            action: 'status',
            uniqueId: user?.data?.uniqueId,
            token: apiKey,
            version: 'v3'
          })
        )

        resolve(websocket.current as WebSocket)
      }

      websocket.current.onclose = (e) => {
        console.log('Websocket disconnected', e)

        setWebsocketState(WebSocketState.CLOSED)
        websocket.current = null

        if (!e.code || e.code !== 1005) {
          // 1005 is the code for a normal close from disconnect()
          console.log('Reconnecting to websocket...')
          connect()
        }
      }

      websocket.current.onerror = (e) => {
        setWebsocketState(WebSocketState.CLOSED)
        console.error('Websocket error', e)
        reject(e)
      }

      websocket.current.onmessage = handleWebsocketMessage
    })
  }

  const disconnect = () => {
    console.log('Disconnect from websocket?')
    if (!websocket?.current) return

    console.log('Disconnecting from websocket...')

    setWebsocketState(WebSocketState.CLOSING)

    websocket.current?.readyState === WebSocket.OPEN && websocket.current?.close()
    websocket.current = null
  }

  return {
    websocket,
    websocketState,

    connect,
    disconnect
  }
}
